import React from 'react';
import { kebabCase } from 'lodash';
import { FormField, Input, InputTypes, INDICATION_TYPES } from 'taboola-ultimate-ui';
import { useEventValueHandler } from 'hooks';
import TooltipSection from 'modules/campaigns/modules/common-campaign-form/components/TooltipSection';
import { withIndication } from 'modules/errors';
import { GTM_EVENTS } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { FormattedMessage, useIntl } from 'modules/taboola-common-frontend-modules/i18n';
import { useFormValidatedValue, validationFunctions } from 'modules/taboola-common-frontend-modules/validations';
import { LANDING_PAGE_URLS } from '../../../consts/urls';
import styles from './title.module.scss';

const InputWithIndication = withIndication(Input);

const validations = [
    {
        validationFn: (value, options, { skipValidations }) => {
            if (skipValidations) {
                return true;
            }

            return validationFunctions.isNotEmptyString(value, options);
        },
        messageId: 'landing.pages.form.validations.title.empty',
        defaultMessage: 'Please enter a page name',
    },
];

const shouldShowUrlPreview = indicationType => {
    return indicationType !== INDICATION_TYPES.ERROR && indicationType !== INDICATION_TYPES.WARNING;
};

export const Title = ({ className, skipValidations, disabled = false }) => {
    const {
        value: title,
        onChange: onChangeTitle,
        indicationData,
        scrollRef,
    } = useFormValidatedValue({
        field: 'title',
        validations,
        validationDependencies: { skipValidations },
    });
    const changeHandler = useEventValueHandler(onChangeTitle);
    const { formatMessage } = useIntl();
    const placeholder = formatMessage({
        id: 'landing.pages.form.title.placeholder.updated',
        defaultMessage: 'e.g., summer-sale-2025',
    });

    // URL Preview logic - convert to kebab-case
    const kebabTitle = title ? kebabCase(title) : placeholder.replace('e.g., ', '');
    const previewUrl = `${LANDING_PAGE_URLS.PRODUCTION}${kebabTitle}`;

    return (
        <FormField
            ref={scrollRef}
            inputId="title"
            label={
                <FormattedMessage
                    id="landing.pages.form.title.input.label.updated"
                    defaultMessage="Enter the advertorial URL (slug)"
                />
            }
            helpText={
                <TooltipSection>
                    <FormattedMessage
                        id="landing.pages.form.title.input.tooltip"
                        defaultMessage="This appears in your reports and in the article's URL."
                    />
                </TooltipSection>
            }
        >
            <InputWithIndication
                id="title"
                placeholder={placeholder}
                type={InputTypes.TEXT}
                value={title ?? ''}
                onChange={changeHandler}
                disabled={disabled}
                data-metrics-event-name={GTM_EVENTS.USABILITY}
                data-metrics-component="title"
                inputClass={className}
                inputWrapperClass={className}
                {...indicationData}
            />
            {shouldShowUrlPreview(indicationData.indicationType) && (
                <div className={styles['url-preview']}>{previewUrl}</div>
            )}
        </FormField>
    );
};
